/* eslint-disable no-console */
import { Strings } from '@/lib/constants/strings'

export type DateInput = Date | string | null | undefined

/**
 * Returns the three-letter abbreviated month name for a given date string.
 *
 * @param {string} dateStr - The date string in the format 'YYYY-MM-DD'.
 * @returns {string} The abbreviated month name (e.g., 'Jan', 'Feb', 'Mar').
 */
export function getMonthAbbreviation(dateStr: string): string {
	const date = new Date(dateStr + 'T00:00:00Z') //Appended string enforces UTC
	const month = date.toLocaleString('default', {
		month: 'short',
		timeZone: 'UTC',
	})

	return month
}

/**
 * Converts a Date object to a string in the format 'YYYY-MM-DD'.
 *
 * @param {Date} date - The date to be formatted.
 * @returns {string} The formatted date string.
 */
export const getDateStr = (
	date: Date | null,
	format: string = 'yyyy-mm-dd'
): string => {
	if (!(date instanceof Date) || isNaN(date.getTime())) {
		return ''
	}

	const year = date.getUTCFullYear()
	const month = String(date.getUTCMonth() + 1).padStart(2, '0')
	const day = String(date.getUTCDate()).padStart(2, '0')

	switch (format.toLowerCase()) {
		case 'mm-dd-yyyy':
			return `${month}-${day}-${year}`
		case 'dd-mm-yyyy':
			return `${day}-${month}-${year}`
		case 'yyyy-mm-dd':
		default:
			return `${year}-${month}-${day}`
	}
}

/**
 * Converts a Date object to a string in the format 'Month DD, YYYY'.
 *
 * @param {Date} date - The date to be formatted.
 * @returns {string} The formatted date string.
 */
export const getDateFormalStr = (date: Date): string => {
	const year = date.getUTCFullYear()
	const monthNames = [
		'January',
		'February',
		'March',
		'April',
		'May',
		'June',
		'July',
		'August',
		'September',
		'October',
		'November',
		'December',
	]
	const month = monthNames[date.getUTCMonth()]
	const day = date.getUTCDate()

	return `${month} ${day}, ${year}`
}

/**
 * Gets the start date of the Last Twelve Months (LTM) period.
 * The start date is defined as the first day of the month one year ago from the current date.
 *
 * @returns {Date} The start date of the LTM period.
 */
export const getLTMStart = (): Date => {
	const date = new Date()
	const year = date.getUTCFullYear() - 1
	const month = date.getUTCMonth()

	return new Date(Date.UTC(year, month, 1))
}

export const getListingStartDate = (): Date => {
	const date = new Date()
	const year = date.getUTCFullYear() - 2 // Two years ago
	const month = date.getUTCMonth() // Current month
	return new Date(Date.UTC(year, month, 1)) // First day of the month
}

export const getListingEndDate = (): Date => {
	const date = new Date()
	const year = date.getUTCFullYear() // Current year
	const month = date.getUTCMonth() // Current month
	const dateOfMonth = date.getUTCDate()
	return new Date(Date.UTC(year, month, dateOfMonth)) // First day of the month
}
/**
 * Gets the start date for comparison purposes.
 *
 * This function calculates a date that is exactly two years prior to the current date.
 * The month is set to January if `getTotals` is true, otherwise it defaults to the current month.
 * The day is set to the first day of the month.
 *
 * @param {boolean} [getTotals=false] - If true, the month is set to January (0). Otherwise, it uses the current month.
 * @returns {Date} The start date for comparison, which is two years prior to the current date.
 *
 * @example
 * ```typescript
 * const compareStartDate = getCompareStart(true); // Returns a date two years prior to the current date, with the month set to January and the day set to the 1st.
 * const compareStartDate = getCompareStart(); // Returns a date two years prior to the current date, with the same month as the current date and the day set to the 1st.
 * ```
 */
export const getCompareStart = (getTotals?: boolean): Date => {
	const date = new Date()
	const year = date.getUTCFullYear() - 1
	const month = getTotals ? 0 : date.getUTCMonth()

	return new Date(Date.UTC(year, month, 1))
}

/**
 * Calculates a start date for revenue data, going back 3 years from the current date.
 *
 * @param getTotals - Optional flag that determines which month to use.
 *                    If true, January (month 0) will be used.
 *                    If false or undefined, the current month will be used.
 * @returns A Date object set to the first day of the specified month, 3 years ago in UTC time.
 */
export const getRevenueStartYear = (getTotals?: boolean): Date => {
	const date = new Date()
	const year = date.getUTCFullYear() - 3
	const month = getTotals ? 0 : date.getUTCMonth()

	return new Date(Date.UTC(year, month, 1))
}

/**
 * Returns a Date object representing the first day of December
 * of the current year in UTC.
 *
 * @returns {Date} A Date object set to December 1st of the current year.
 */
export const getYearEnd = (): Date => {
	const date = new Date()
	const year = date.getUTCFullYear()
	return new Date(Date.UTC(year, 11, 1))
}

/**
 * Calculates the revenue year end date as December 31st of the next year.
 * The date is created using UTC timezone.
 *
 * @returns A Date object representing December 31st of the current year in UTC timezone
 */
export const calculateRevenueYearEnd = (): Date => {
	const date = new Date()
	const year = date.getUTCFullYear() + 1
	return new Date(Date.UTC(year, 11, 31))
}

/**
 * Checks if the current date is after December 31st of the specified year.
 * Used to determine if goal setting should be disabled for a given year.
 *
 * @param year - The year to check against
 * @returns true if current date is after December 31st of the specified year, false otherwise
 */
export const isAfterYearEnd = (year: number): boolean => {
	const currentDate = new Date()
	const yearEndDate = new Date(Date.UTC(year, 11, 31, 23, 59, 59, 999)) // December 31st end of day
	return currentDate > yearEndDate
}

/**
 * Returns the abbreviated month name for a given UTC date string.
 * '2024-11-13T04:20:42.000Z' would return "Nov".
 *
 * @param {string} utcDate - The UTC date string to extract the month from.
 * @returns {string} The abbreviated month name (e.g., "Jan", "Feb", "Mar").
 */
export const getUtcMonthAbbreviation = (utcDate: string): string => {
	const date = new Date(utcDate)
	const monthNames = [
		'Jan',
		'Feb',
		'Mar',
		'Apr',
		'May',
		'Jun',
		'Jul',
		'Aug',
		'Sep',
		'Oct',
		'Nov',
		'Dec',
	]
	return monthNames[date.getUTCMonth()]
}

// Local UTC-based formatter: "Mon DD" for current year, else "Mon DD, YYYY"
export const formatAbbrevWithOptionalYear = (utcDateStr: string): string => {
	if (!utcDateStr) return ''
	const d = new Date(utcDateStr)
	if (isNaN(d.getTime())) return ''
	const month = getUtcMonthAbbreviation(utcDateStr)
	const day = d.getUTCDate()
	const year = d.getUTCFullYear()
	const currentYear = new Date().getUTCFullYear()
	return year === currentYear ? `${month} ${day}` : `${month} ${day}, ${year}`
}

/**
 * Formats a given UTC date string into a user-friendly string based on the user's time zone.
 * The output format is "Month Day, Year at Time (TimeZone)".
 *
 * @param {string} utcDate - The UTC date string to be formatted.
 * @param {string} userTimeZone - The user's time zone identifier (e.g., "America/New_York").
 * @returns {string} The formatted date string or an error message if the input is invalid.
 *
 * @example
 * ```typescript
 * const formattedDate = graphUpdatedTime("2023-10-05T14:48:00Z", "America/New_York");
 * console.log(formattedDate); // "Oct 5, 2023 at 10:48 a.m. (ET)"
 * ```
 */
export const graphUpdatedTime = (
	utcDate: string,
	userTimeZone: string,
	showLabel: boolean = true,
	longMonth: boolean = false
): string => {
	if (!utcDate) {
		return Strings.UPDATED_TIME_LABEL_INVALID
	}

	const date = new Date(utcDate)

	if (isNaN(date.getTime())) {
		console.error('Invalid date passed to graphUpdatedTime:', utcDate)
		return Strings.UPDATED_TIME_LABEL_INVALID
	}

	const options = {
		year: 'numeric' as const,
		month: (longMonth ? 'long' as const : 'short' as const),
		day: 'numeric' as const,
		hour: 'numeric' as const,
		minute: 'numeric' as const,
		hour12: true,
		timeZone: userTimeZone,
		timeZoneName: 'shortGeneric' as const,
	}

	const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date)

	const convertToGraphFormat = (
		dateString: string,
		showLabel: boolean
	): string => {
		let monthDayPart, yearPart, timeZonePart
		// iphone has an 'at' in the date string
		if (dateString.includes(' at ')) {
			const parts = dateString.split(' at ')
			if (parts.length !== 2) {
				console.error('Unexpected date format:', dateString)
				return Strings.UPDATED_TIME_LABEL_INVALID
			}
			;[monthDayPart, yearPart] = parts[0].split(', ')
				;[, timeZonePart] = parts
		} else {
			const parts = dateString.split(', ')
			if (parts.length !== 3) {
				console.error('Unexpected date format:', dateString)
				return Strings.UPDATED_TIME_LABEL_INVALID
			}
			;[monthDayPart, yearPart, timeZonePart] = parts
		}

		if (!monthDayPart || !yearPart || !timeZonePart) {
			console.error('Unexpected date format:', dateString)
			return Strings.UPDATED_TIME_LABEL_INVALID
		}

		const timeZoneMatch = /(\d{1,2}:\d{2})\s(AM|PM)\s(\w+)/.exec(timeZonePart)
		if (!timeZoneMatch) {
			console.error('Unexpected time zone format:', timeZonePart)
			return Strings.UPDATED_TIME_LABEL_INVALID
		}
		const [, time, period, timeZone] = timeZoneMatch

		const formattedTime = period === 'AM' ? 'a.m.' : 'p.m.'
		const timeStamp = `${monthDayPart}, ${yearPart} at ${time} ${formattedTime} (${timeZone})`
		// Assemble the final format: "Month Day, Year at Time (TimeZone)"
		return showLabel ? `${Strings.UPDATED_TIME_INTIAL_LABEL_VALID} ${timeStamp}` : timeStamp
	}

	return convertToGraphFormat(formattedDate, showLabel)
}

/**
 * Calculates tenure
 * The tenure is calculated as the difference between the input date and the second date (or today if no second date is provided).
 * If the input date is null, undefined, or empty, the function returns an empty string.
 * If the tenure is less than 1 month, the function returns '< 1 mo.'.
 * If the tenure is less than 12 months, the function returns the number of months. Ex. '10 mos'.
 * If the tenure is 12 months or more, the function returns the number of years and months. Ex. '1 yr. 3 mos'.
 * @param {Date | string | null | undefined} inputDate - The start date.
 * @param {Date | string | null | undefined} secondDate - The end date (optional). Defaults to today.
 * @returns {string} - The tenure
 */
export const getTenure = (
	inputDate: DateInput,
	secondDate?: DateInput
): string => {
	if (!inputDate) {
		return ''
	}

	const now =
		secondDate === '9999-12-31'
			? new Date()
			: new Date(secondDate || new Date())
	const date = new Date(inputDate)

	if (isNaN(date.getTime()) || isNaN(now.getTime())) {
		return ''
	}

	// No future dates
	if (now < date) {
		return ''
	}

	// Get today's day in UTC
	const today = now.getDate()

	// Determine whether to use the first or last day of the month
	const adjustedDate =
		date.getDate() <= today
			? new Date(date.getFullYear(), date.getMonth(), 1) // first day of the month
			: new Date(date.getFullYear(), date.getMonth() + 1, 0) // last day of the month

	const calculateMonthDifference = (date1: Date, date2: Date): number => {
		const year1 = date1.getUTCFullYear()
		const month1 = date1.getUTCMonth()
		const year2 = date2.getUTCFullYear()
		const month2 = date2.getUTCMonth()

		return (year2 - year1) * 12 + (month2 - month1)
	}

	const diffInMonths = calculateMonthDifference(adjustedDate, now)
	const diffInYears = Math.floor(diffInMonths / 12)
	const remainingMonths = diffInMonths % 12

	if (diffInMonths < 1) {
		return '< 1 mo.'
	}
	if (diffInMonths < 12) {
		return `${diffInMonths} mo${diffInMonths > 1 ? 's' : ''}.`
	}

	const yearString = `${diffInYears} yr${diffInYears > 1 ? 's' : ''}.`
	let monthString = ''
	if (remainingMonths > 0) {
		monthString = ` ${remainingMonths} mo${remainingMonths > 1 ? 's' : ''}.`
	}
	return `${yearString}${monthString}`
}

/**
 * Converts a month string to a month number.
 *
 * @param {string} monthStr - The month string (e.g., "January", "Feb", "Mar").
 * @returns {number} The month number (1 for January, 2 for February, etc.).
 */
export function convertMonthStringToNumber(monthStr: string): number {
	const monthMap: { [key: string]: number } = {
		Jan: 1,
		Feb: 2,
		Mar: 3,
		Apr: 4,
		May: 5,
		Jun: 6,
		Jul: 7,
		Aug: 8,
		Sep: 9,
		Oct: 10,
		Nov: 11,
		Dec: 12,
	}

	const normalizedMonthStr = monthStr.trim()
	const monthNumber = monthMap[normalizedMonthStr]

	if (!monthNumber) {
		throw new Error(`Invalid month string: ${monthStr}`)
	}

	return monthNumber
}

/**
 * Joins two arrays as key-value pairs.
 *
 * @param {string[]} keys - The array of keys (e.g., months).
 * @param {number[]} values - The array of values.
 * @returns {Record<string, number>} An object with keys and their corresponding values.
 */
export function joinArraysAsKeyValuePairs(
	keys: string[],
	values: number[]
): Record<string, number> {
	if (keys.length !== values.length) {
		throw new Error('Keys and values arrays must have the same length.')
	}
	return keys.reduce(
		(acc, key, index) => {
			acc[key] = values[index]
			return acc
		},
		{} as Record<string, number>
	)
}

/**
 * Gets formatted date only for a specific date
 * @param {Date} date - The date to format
 * @param {string} timeZone - The timezone to format for
 * @returns {string} Formatted date string (e.g., "Apr 5, 2025")
 */
export const formatDateMMMDDYYYY = (
	date: Date,
	timeZone: string = 'UTC'
): string => {
	const dateOptions: Intl.DateTimeFormatOptions = {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		timeZone,
	}

	return date.toLocaleDateString('en-US', dateOptions)
}

/**
 * Gets the date range for the last 30 days.
 * Returns from 30 days ago to today.
 *
 * @returns {Object} An object containing fromDt and toDt as Date objects
 * @example
 * // If today is August 15, 2025
 * // Returns: { fromDt: 2025-07-16, toDt: 2025-08-15 }
 */
export const getLast30DaysDateRange = (): { fromDt: Date; toDt: Date } => {
	const toDt = new Date()
	const fromDt = new Date()
	fromDt.setUTCDate(toDt.getUTCDate() - 29)
	return { fromDt, toDt }
}

/**
 * Formats a UTC date into:
 * - "Today" if it's today in UTC
 * - "Yesterday" if it's yesterday in UTC
 * - otherwise "Month D, YYYY"
 *
 * NOTE: compares by UTC calendar day to avoid timezone drift.
 */
export const formatDateTodayYesterdayOrFormalUTC = (
	dateInput: DateInput
): string => {
	if (!dateInput) return ''

	const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput
	if (!(date instanceof Date) || isNaN(date.getTime())) return ''

	const targetUtcMidnight = new Date(
		Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
	)

	const now = new Date()
	const todayUtcMidnight = new Date(
		Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
	)

	const diffDays =
		(todayUtcMidnight.getTime() - targetUtcMidnight.getTime()) /
		(24 * 60 * 60 * 1000)

	if (diffDays === 0) return 'Today'
	if (diffDays === 1) return 'Yesterday'

	return getDateFormalStr(targetUtcMidnight)
}

/**
 * Formats a UTC date string into a time-only string like "3:00 a.m." or "12:00 p.m."
 * @param {string} utcDate - The UTC date string to be formatted.
 * @param {string} timeZone - The user's time zone identifier (e.g., "America/New_York").
 * @returns {string} The formatted time string or an empty string if invalid.
 *
 * @example
 * formatTimeOnly("2025-02-23T03:00:00Z", "America/New_York"); // "10:00 p.m."
 */
export const formatTimeOnly = (
	utcDate: string,
	timeZone: string = 'UTC'
): string => {
	const date = new Date(utcDate);
	if (isNaN(date.getTime())) return '';

	const options: Intl.DateTimeFormatOptions = {
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
		timeZone,
	}

	// Example output: "3:00 AM"
	let formatted = date.toLocaleTimeString('en-US', options);

	// Convert "AM"/"PM" to "a.m."/"p.m."
	formatted = formatted.replace('AM', 'a.m.').replace('PM', 'p.m.')

	// Remove any leading zero in hour (e.g., "03:00 a.m." -> "3:00 a.m.")
	formatted = formatted.replace(/^0/, '')

	return formatted;
}
